import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import * as R from 'ramda'
import { Defaults } from '@pbt/pbt-ui-components'

import {
  fetchMoreItemsForTimeline,
  updateTimelineClientData,
} from '~/store/actions/timeline'
import { getFinanceIsSaving } from '~/store/reducers/finance'
import { getOrdersIsSending } from '~/store/reducers/orders'
import {
  getTimelineIsLoading,
  getTimelineList,
  getTimelineTotalCount,
} from '~/store/reducers/timeline'
import { getUrlSearchParam } from '~/utils'

import Timeline, { type TimelineProps } from './Timeline'

export interface TimelineComponentProps
  extends Omit<
    TimelineProps,
    | 'data'
    | 'isItemLoaded'
    | 'loadMoreItems'
    | 'totalCount'
    | 'initialItemParam'
  > {}

const TimelineComponent = ({
  clientId,
  patientId,
  ...rest
}: TimelineComponentProps) => {
  const dispatch = useDispatch()
  const location = useLocation()

  const list = useSelector(getTimelineList)
  const totalCount = useSelector(getTimelineTotalCount)
  const isFinanceSaving = useSelector(getFinanceIsSaving)
  const isOrdersSending = useSelector(getOrdersIsSending)
  const isLoading = useSelector(getTimelineIsLoading)

  const initialItemParam = isLoading
    ? 0
    : Number(getUrlSearchParam('initialItem', location.search)) || 0

  const queryFrom = Math.max(
    initialItemParam - Defaults.INFINITE_LIST_BATCH_LOAD_COUNT,
    0,
  )
  const queryTo = initialItemParam
    ? initialItemParam + Defaults.INFINITE_LIST_BATCH_LOAD_COUNT
    : Defaults.INFINITE_LIST_BATCH_LOAD_COUNT

  useEffect(() => {
    if (!isFinanceSaving && !isOrdersSending) {
      dispatch(
        updateTimelineClientData({
          clientId,
          patientId,
          from: queryFrom,
          to: queryTo,
          fillPrecedingWithNull: queryFrom > 0,
        }),
      )
    }
  }, [clientId, patientId, isFinanceSaving, isOrdersSending])

  const isItemLoaded = (index: number) => Boolean(list[index])

  const loadMoreItems = (startIndex: number, endIndex: number) => {
    if (!R.isEmpty(list)) {
      dispatch(fetchMoreItemsForTimeline(startIndex, endIndex))
    }
  }

  return (
    <Timeline
      clientId={clientId}
      data={list}
      initialItemParam={initialItemParam}
      isItemLoaded={isItemLoaded}
      loadMoreItems={loadMoreItems}
      patientId={patientId}
      totalCount={totalCount}
      {...rest}
    />
  )
}

export default TimelineComponent
